import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  Flex,
  Text,
  Button,
  Select,
  Separator,
  IconButton,
  Box,
} from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../context/ToastContext";
import { ToastAlert } from "components";
import { useProfileContext, useLocationContext } from "context";
import { useGetAllBrands, useGetLeatContactAttributes, useSaveLeatMappings } from "hooks";
import { LeatDataFieldsSection } from "./LeatDataFieldsSection";
import { normalizeLeatFieldMappings } from "./leatFieldMappings";

export function LeatMappingDialog({
  isOpen,
  onOpenChange,
  shops = [],
  initialMappings = [],
  initialFieldMappings = null,
  reconnectAction = null,
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setToastMessage } = useToast();
  const { userProfile } = useProfileContext();
  const { data: brandsData } = useGetAllBrands(userProfile?.organisation_id);
  const { locationsData } = useLocationContext();
  const saveLeatMappings = useSaveLeatMappings();

  const [leatShops, setLeatShops] = useState([]);
  const [bconnectBrands, setBconnectBrands] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);
  const [locationMappings, setLocationMappings] = useState({});
  const [brandListSelections, setBrandListSelections] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasRestoredMappings, setHasRestoredMappings] = useState(false);
  const [fieldMappings, setFieldMappings] = useState(() =>
    normalizeLeatFieldMappings(initialFieldMappings)
  );

  const { data: attributesData } = useGetLeatContactAttributes(
    userProfile?.organisation_id,
    isOpen
  );
  const leatAttributes = attributesData?.data?.attributes || [];

  useEffect(() => {
    if (!isOpen) return;

    setLeatShops(
      (Array.isArray(shops) ? shops : []).map((l) => ({
        id: String(l.uuid ?? l.id ?? ""),
        name: l.name,
      }))
    );
    setBrandListSelections({});

    const next = {};
    (Array.isArray(initialMappings) ? initialMappings : []).forEach((m) => {
      if (m.bconnect_location_id == null || !m.leat_shop_uuid) return;
      const key = String(m.bconnect_location_id);
      next[key] = {
        leat_shop_uuid: String(m.leat_shop_uuid),
        isMapped: true,
      };
    });

    setLocationMappings(next);
    setHasRestoredMappings(
      Object.keys(next).length > 0 &&
        (reconnectAction === "resume" || reconnectAction === "continue_from_now")
    );
    setFieldMappings(normalizeLeatFieldMappings(initialFieldMappings));
  }, [isOpen, shops, initialMappings, reconnectAction, initialFieldMappings]);

  useEffect(() => {
    if (!brandsData || !locationsData) return;
    const defaultBrand = brandsData.find((b) => b.is_default);
    setBconnectBrands(brandsData.filter((b) => !b.is_default) || []);
    setIndependentLocations(
      locationsData.filter(
        (loc) => loc.brand_id === defaultBrand?.id || loc.brand_id === null
      ) || []
    );
  }, [brandsData, locationsData]);

  const brandLocationsMap = useMemo(() => {
    const map = {};
    (bconnectBrands || []).forEach((b) => {
      const sourceBrand = (brandsData || []).find((bd) => String(bd.id) === String(b.id));
      if (sourceBrand && Array.isArray(sourceBrand.locations)) {
        map[b.id] = sourceBrand.locations;
      } else {
        map[b.id] = (locationsData || []).filter(
          (loc) => String(loc.brand_id) === String(b.id)
        );
      }
    });
    return map;
  }, [bconnectBrands, brandsData, locationsData]);

  const handleLocationListChange = (bconnectLocationId, leatListId) => {
    const key = String(bconnectLocationId);
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      const existing = next[key] || { leat_shop_uuid: "", isMapped: false };
      next[key] = { ...existing, leat_shop_uuid: leatListId, isMapped: Boolean(leatListId) };
      return next;
    });
  };

  const handleBrandListSelectionChange = (brandId, listId) => {
    setBrandListSelections((prev) => ({ ...(prev || {}), [String(brandId)]: listId }));
  };

  const handleApplyBrandList = (brandId) => {
    const selectedList = brandListSelections[String(brandId)];
    if (!selectedList) return;
    const brandLocs = brandLocationsMap[brandId] || [];
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      brandLocs.forEach((loc) => {
        const locId = loc.id ?? loc.value ?? loc.location_id;
        const key = String(locId);
        const existing = next[key] || { leat_shop_uuid: "", isMapped: false };
        next[key] = {
          ...existing,
          leat_shop_uuid: selectedList,
          isMapped: Boolean(selectedList),
        };
      });
      return next;
    });
  };

  const handleSaveLocationMappings = async () => {
    try {
      setIsLoading(true);

      const allLocations = [
        ...(independentLocations || []),
        ...(Object.values(brandLocationsMap || {}).flat() || []),
      ];

      const locationPayload = allLocations
        .map((loc) => {
          const locId = loc.id ?? loc.value ?? loc.location_id;
          const key = String(locId);
          const curr = locationMappings[key] || {};
          const currList = curr.leat_shop_uuid || null;
          if (!currList) return null;
          const leatListObj = leatShops.find((bl) => String(bl.id) === String(currList));
          return {
            bconnect_location_id: locId,
            bconnect_location_name: loc.name ?? loc.label ?? "",
            leat_shop_uuid: currList,
            leat_shop_name: leatListObj?.name || "",
          };
        })
        .filter(Boolean);

      const result = await saveLeatMappings.mutateAsync({
        organisation_id: userProfile.organisation_id,
        brand_mappings: [],
        location_mappings: locationPayload,
        field_mappings: fieldMappings,
      });

      if (result?.data?.status !== "success") {
        throw new Error(result?.data?.message || "Failed to save location mappings");
      }

      queryClient.invalidateQueries(["leat-status"]);
      queryClient.invalidateQueries(["leat-mappings", userProfile.organisation_id]);
      queryClient.invalidateQueries(["leat-contact-attributes", userProfile.organisation_id]);
      onOpenChange(false);
      navigate("/activation-success", {
        state: {
          description: "Your customers will now sync with your selected Leat shops.",
          integrationType: "leat",
        },
      });
    } catch (err) {
      console.error("Error saving Leat location mappings:", err);
      setToastMessage(err?.message || "Failed to save location mappings");
    } finally {
      setIsLoading(false);
    }
  };

  const renderLocationRow = (loc) => {
    const locId = loc.id ?? loc.value ?? loc.location_id;
    const key = String(locId);
    const mapping = locationMappings[key] || {};
    return (
      <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
        <Flex style={{ width: "48%", minWidth: 0 }}>
          <Flex
            style={{
              width: "100%",
              backgroundColor: "var(--gray-3)",
              border: "1px solid var(--gray-a7)",
              borderRadius: "var(--radius-2)",
              padding: "0 var(--space-2)",
              height: "34px",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            <Text
              size="2"
              title={loc.name ?? loc.label ?? ""}
              style={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                width: "100%",
                color: "var(--gray-a11)",
              }}
            >
              {loc.name ?? loc.label ?? ""}
            </Text>
          </Flex>
        </Flex>

        <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
          <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
            <ArrowRightIcon width="16" height="16" />
          </IconButton>
        </Flex>

        <Flex style={{ width: "48%", minWidth: 0 }}>
          <Select.Root
            value={mapping.leat_shop_uuid || ""}
            onValueChange={(v) => handleLocationListChange(key, v)}
            style={{ width: "100%" }}
          >
            <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
              <div
                style={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
                title={
                  mapping.leat_shop_uuid
                    ? leatShops.find((bl) => String(bl.id) === String(mapping.leat_shop_uuid))
                        ?.name || ""
                    : "Select a Leat shop"
                }
              >
                {mapping.leat_shop_uuid
                  ? leatShops.find((bl) => String(bl.id) === String(mapping.leat_shop_uuid))
                      ?.name || ""
                  : "Select a Leat shop"}
              </div>
            </Select.Trigger>
            <Select.Content side="bottom" align="start" sideOffset={4}>
              {leatShops.map((bl) => (
                <Select.Item key={bl.id} value={bl.id} title={bl.name}>
                  {bl.name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>
      </Flex>
    );
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Content
        maxWidth="720px"
        style={{
          height: "min(760px, calc(100vh - 96px))",
          padding: "28px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Flex direction="column" gap="4" style={{ flex: "1 1 auto", minHeight: 0 }}>
          <Flex direction="column" gap="2" style={{ flex: "0 0 auto" }}>
            <Dialog.Title>Map locations to Leat shops</Dialog.Title>
            <Dialog.Description size="2" style={{ color: "var(--gray-a11)" }}>
              {hasRestoredMappings
                ? "Your previous location mappings have been restored. Review them below, change anything if needed, then save to finish connecting."
                : "Choose which Leat shop each b connect location should sync to. You can map each location independently."}
            </Dialog.Description>
          </Flex>

          <Box
            style={{
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
              paddingRight: "var(--space-2)",
            }}
          >
            <Flex direction="column" gap="4">
              <Flex direction="column" gap="3">
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">
                    Brands
                  </Text>
                  <Text size="1" color="gray">
                    Group: b connect brands with each of their locations.
                  </Text>
                </Flex>

                <Flex direction="row" justify="between" style={{ width: "100%" }}>
                  <Text size="2" weight="medium" style={{ width: "48%" }}>
                    b connect location
                  </Text>
                  <Text size="2" weight="medium" style={{ width: "48%" }}>
                    Leat shop
                  </Text>
                </Flex>

                {(bconnectBrands || []).length > 0 ? (
                  (bconnectBrands || []).map((b) => {
                    const brandLocations = brandLocationsMap[b.id] || [];
                    return (
                      <Flex key={b.id} direction="column" gap="2" style={{ width: "100%" }}>
                        <Flex
                          direction="row"
                          style={{ width: "100%", alignItems: "center", gap: 8 }}
                        >
                          <Text
                            size="2"
                            weight="medium"
                            style={{
                              flex: "1 1 0%",
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            [{b.name}]
                          </Text>

                          {(brandLocations || []).length > 0 && (
                            <Flex
                              style={{
                                marginLeft: "8px",
                                gap: 8,
                                alignItems: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Select.Root
                                value={brandListSelections[String(b.id)] || ""}
                                onValueChange={(v) => handleBrandListSelectionChange(b.id, v)}
                                style={{ width: 200, minWidth: 120, flexShrink: 0 }}
                              >
                                <Select.Trigger
                                  placeholder="Apply shop to brand..."
                                  style={{ width: "200px", boxSizing: "border-box" }}
                                >
                                  <div
                                    style={{
                                      textOverflow: "ellipsis",
                                      overflow: "hidden",
                                      whiteSpace: "nowrap",
                                      width: "100%",
                                    }}
                                  >
                                    {brandListSelections[String(b.id)]
                                      ? leatShops.find(
                                          (bl) =>
                                            String(bl.id) ===
                                            String(brandListSelections[String(b.id)])
                                        )?.name || ""
                                      : "Apply shop to brand..."}
                                  </div>
                                </Select.Trigger>
                                <Select.Content side="bottom" align="start" sideOffset={4}>
                                  {leatShops.map((bl) => (
                                    <Select.Item key={bl.id} value={bl.id} title={bl.name}>
                                      {bl.name}
                                    </Select.Item>
                                  ))}
                                </Select.Content>
                              </Select.Root>
                              <Button
                                size="1"
                                variant="soft"
                                onClick={() => handleApplyBrandList(b.id)}
                                style={{ flexShrink: 0 }}
                              >
                                Apply
                              </Button>
                            </Flex>
                          )}
                        </Flex>

                        {(brandLocations || []).length > 0 ? (
                          brandLocations.map(renderLocationRow)
                        ) : (
                          <Text size="2" style={{ color: "var(--gray-a9)" }}>
                            No locations for this brand
                          </Text>
                        )}
                      </Flex>
                    );
                  })
                ) : (
                  <Text size="2" color="gray">
                    No brands found.
                  </Text>
                )}
              </Flex>

              <Separator orientation="horizontal" size="4" />

              <Flex direction="column" gap="3">
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">
                    Locations
                  </Text>
                  <Text size="1" color="gray">
                    Map independent b connect locations to a Leat shop.
                  </Text>
                </Flex>

                <Flex direction="row" justify="between" style={{ width: "100%" }}>
                  <Text size="2" weight="medium" style={{ width: "48%" }}>
                    b connect location
                  </Text>
                  <Text size="2" weight="medium" style={{ width: "48%" }}>
                    Leat shop
                  </Text>
                </Flex>

                {(independentLocations || []).length > 0 ? (
                  independentLocations.map(renderLocationRow)
                ) : (
                  <Text size="2" color="gray">
                    No locations found.
                  </Text>
                )}
              </Flex>

              <Separator orientation="horizontal" size="4" />

              <Flex direction="column" gap="3">
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">
                    Data fields
                  </Text>
                  <Text size="1" color="gray">
                    Map captive portal fields to Leat contact columns. Fields a guest did not
                    enter are skipped. Leave as Don't sync to never send that field. Missing
                    Leat columns are created when you save.
                  </Text>
                </Flex>
                <LeatDataFieldsSection
                  fieldMappings={fieldMappings}
                  attributes={leatAttributes}
                  onChange={(source, value) =>
                    setFieldMappings((prev) => ({ ...prev, [source]: value }))
                  }
                />
              </Flex>
            </Flex>
          </Box>

          <Flex justify="end" gap="3" style={{ flex: "0 0 auto" }}>
            <Dialog.Close>
              <Button size="2" variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              size="2"
              variant="soft"
              color="accent"
              loading={isLoading}
              onClick={handleSaveLocationMappings}
            >
              Save and finish
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>

      <div style={{ position: "fixed", zIndex: 2147483647, pointerEvents: "none" }}>
        <ToastAlert />
      </div>
    </Dialog.Root>
  );
}
